import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["align", "autoAlign", "badgeCount", "children", "className", "closeOnActivation", "defaultOpen", "disabled", "dropShadow", "enterDelayMs", "highContrast", "kind", "label", "leaveDelayMs", "wrapperClasses", "size", "isSelected"];
/**
 * Copyright IBM Corp. 2016, 2023
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React from 'react';
import cx from 'classnames';
import '../Tooltip/DefinitionTooltip.js';
import { Tooltip } from '../Tooltip/Tooltip.js';
import { useId } from '../../internal/useId.js';
import { usePrefix } from '../../internal/usePrefix.js';
import ButtonBase from '../Button/ButtonBase.js';
import deprecateValuesWithin from '../../prop-types/deprecateValuesWithin.js';
import { BadgeIndicator } from '../BadgeIndicator/index.js';
import { mapPopoverAlign } from '../../tools/mapPopoverAlign.js';
var IconButtonKinds = ['primary', 'secondary', 'ghost', 'tertiary'];
var IconButton = /*#__PURE__*/React.forwardRef(function IconButton(_ref, ref) {
  var align = _ref.align,
    _ref$autoAlign = _ref.autoAlign,
    autoAlign = _ref$autoAlign === void 0 ? false : _ref$autoAlign,
    badgeCount = _ref.badgeCount,
    children = _ref.children,
    className = _ref.className,
    _ref$closeOnActivatio = _ref.closeOnActivation,
    closeOnActivation = _ref$closeOnActivatio === void 0 ? true : _ref$closeOnActivatio,
    _ref$defaultOpen = _ref.defaultOpen,
    defaultOpen = _ref$defaultOpen === void 0 ? false : _ref$defaultOpen,
    disabled = _ref.disabled,
    _ref$dropShadow = _ref.dropShadow,
    dropShadow = _ref$dropShadow === void 0 ? false : _ref$dropShadow,
    _ref$enterDelayMs = _ref.enterDelayMs,
    enterDelayMs = _ref$enterDelayMs === void 0 ? 100 : _ref$enterDelayMs,
    _ref$highContrast = _ref.highContrast,
    highContrast = _ref$highContrast === void 0 ? true : _ref$highContrast,
    kind = _ref.kind,
    label = _ref.label,
    _ref$leaveDelayMs = _ref.leaveDelayMs,
    leaveDelayMs = _ref$leaveDelayMs === void 0 ? 100 : _ref$leaveDelayMs,
    wrapperClasses = _ref.wrapperClasses,
    size = _ref.size,
    isSelected = _ref.isSelected,
    rest = _objectWithoutProperties(_ref, _excluded);
  var prefix = usePrefix();
  var tooltipClasses = cx(wrapperClasses, "".concat(prefix, "--icon-tooltip"), _defineProperty({}, "".concat(prefix, "--icon-tooltip--disabled"), disabled));
  if (badgeCount && (kind !== 'ghost' || size !== 'lg')) {
    console.warn("The prop BadgeCount must be used with hasIconOnly=true, kind='ghost' and size='lg'");
  }
  var badgeId = useId('badge-indicator');
  return /*#__PURE__*/React.createElement(Tooltip, {
    align: align,
    autoAlign: autoAlign,
    closeOnActivation: closeOnActivation,
    className: tooltipClasses,
    defaultOpen: defaultOpen,
    dropShadow: dropShadow,
    enterDelayMs: enterDelayMs,
    highContrast: highContrast,
    label: label,
    leaveDelayMs: leaveDelayMs
  }, /*#__PURE__*/React.createElement(ButtonBase, _extends({}, rest, {
    disabled: disabled,
    kind: kind,
    ref: ref,
    size: size,
    isSelected: isSelected,
    hasIconOnly: true,
    className: className,
    "aria-describedby": rest['aria-describedby'] || badgeCount && badgeId
  }), children, !disabled && badgeCount !== undefined && /*#__PURE__*/React.createElement(BadgeIndicator, {
    id: badgeId,
    count: badgeCount > 0 ? badgeCount : undefined
  })));
});
export { IconButton, IconButtonKinds };